import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import PlayArrowIcon from "@material-ui/icons/PlayArrow"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    criarEstrategiasPadrao,
    excluirMarketingAutomatico,
    executarMarketingAutomatico,
    type MarketingAutomaticoItem,
} from "../../../services/marketingAutomatico"
import { MarketingAutomaticoActionsTypes } from "../context/marketingAutomaticoActions"
import { useMarketingAutomaticoContext } from "../context/useMarketingAutomaticoContext"
import { FormMarketingAutomatico } from "./FormMarketingAutomatico"

const EVENTO_LABEL: Record<string, string> = {
    CADASTRO_SEM_PEDIDO: "Cadastro sem pedido",
    AUSENCIA: "Ausência",
    ANIVERSARIO: "Aniversário",
    CADASTRO: "Cadastro",
    BOAS_VINDAS: "Boas-vindas",
    DIARIAMENTE: "Diariamente",
}

const DIA_LABEL: Record<number, string> = {
    0: "Todos os dias",
    1: "Domingo",
    2: "Segunda",
    3: "Terça",
    4: "Quarta",
    5: "Quinta",
    6: "Sexta",
    7: "Sábado",
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "960px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: { padding: theme.spacing(2) },
    titleText: {
        fontWeight: 700,
        color: "#2c3e50",
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down("sm")]: { fontSize: "1.3rem" },
    },
    botaoPrimario: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "#fff",
        fontWeight: 600,
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    botaoSecundario: {
        borderRadius: 25,
        borderColor: "#4caf50",
        color: "#4caf50",
        fontWeight: 600,
        "&:hover": { backgroundColor: "#e8f5e9" },
    },
    aviso: {
        color: "#7f8c8d",
        fontSize: "0.82rem",
        marginBottom: theme.spacing(2),
        lineHeight: 1.6,
    },
    eventoChip: {
        display: "inline-block",
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        borderRadius: 12,
        padding: "2px 10px",
        fontSize: "0.78rem",
        fontWeight: 600,
    },
    cupomChip: {
        display: "inline-block",
        backgroundColor: "#fff3e0",
        color: "#e65100",
        borderRadius: 12,
        padding: "2px 10px",
        fontSize: "0.78rem",
        fontWeight: 600,
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 180,
    },
    msgCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
    },
}))

export function MarketingAutomaticoComponent() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento, recarregar } = useMarketingAutomaticoContext()
    const { enqueueSnackbar } = useSnackbar()
    const [dialogAberto, setDialogAberto] = useState(false)
    const [itemEditando, setItemEditando] = useState<MarketingAutomaticoItem | null>(null)
    const [termoBusca, setTermoBusca] = useState("")
    const [criandoPadrao, setCriandoPadrao] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const listaFiltrada = termoBusca
        ? dados.lista.filter(i => i.tituloMensagem.toLowerCase().includes(termoBusca.toLowerCase()))
        : dados.lista

    function abrirNovo() {
        setItemEditando(null)
        setDialogAberto(true)
    }

    function abrirEditar(item: MarketingAutomaticoItem) {
        setItemEditando(item)
        setDialogAberto(true)
    }

    async function handleExcluir(item: MarketingAutomaticoItem) {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const response = await excluirMarketingAutomatico(autorizacao, item.id)
        if (response && "retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao excluir estratégia", "error")
            return
        }
        dispatch({ type: MarketingAutomaticoActionsTypes.REMOVER, payload: item.id })
        alertStart("Estratégia excluída com sucesso!", "success")
    }

    async function handleExecutar(item: MarketingAutomaticoItem) {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const response = await executarMarketingAutomatico(autorizacao, item.id)
        if ("retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao executar estratégia", "error")
            return
        }
        alertStart(
            `Marketing processado com sucesso! Clientes impactados: ${response.qtdImpactados}`,
            "success"
        )
    }

    async function handleCriarPadrao() {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setCriandoPadrao(true)
            const response = await criarEstrategiasPadrao(autorizacao)
            if (response && "retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao criar estratégias padrão", "error")
                return
            }
            await recarregar()
            alertStart("Estratégias padrão criadas com sucesso!", "success")
        } finally {
            setCriandoPadrao(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Marketing Automatizado
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                placeholder="Buscar por título..."
                                value={termoBusca}
                                onChange={e => setTermoBusca(e.target.value)}
                                style={{ minWidth: 220 }}
                            />
                        </Box>
                        <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                            <Button
                                variant="outlined"
                                className={classes.botaoSecundario}
                                onClick={handleCriarPadrao}
                                disabled={criandoPadrao}
                                size="small"
                            >
                                {criandoPadrao ? "Criando..." : "Criar estratégias padrão"}
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.botaoPrimario}
                                startIcon={<AddIcon />}
                                onClick={abrirNovo}
                                size="small"
                            >
                                Nova estratégia
                            </Button>
                        </Box>
                    </Box>

                    <Box className={classes.cardContent}>
                        <Typography className={classes.aviso}>
                            Os nomes dos cupons do Marketing Automatizado não podem ser os mesmos
                            dos cupons criados em Cadastros &gt; Cupom de Incentivo. Não se esqueça
                            de escrever corretamente o nome do cupom na mensagem, pois somente assim
                            os clientes conseguirão utilizá-lo.
                        </Typography>

                        <TableContainer style={{ overflowX: "auto" }}>
                            <Table size="small" style={{ width: "100%", minWidth: 810 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 120 }}>Evento</TableCell>
                                        <TableCell style={{ minWidth: 160 }}>Título</TableCell>
                                        <TableCell style={{ minWidth: 180 }}>Mensagem</TableCell>
                                        <TableCell style={{ minWidth: 130 }}>Detalhes</TableCell>
                                        <TableCell style={{ minWidth: 140 }}>Cupom</TableCell>
                                        <TableCell style={{ minWidth: 80 }} align="center">
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {listaFiltrada.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                align="center"
                                                style={{ color: "#7f8c8d" }}
                                            >
                                                Nenhuma estratégia encontrada.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {listaFiltrada.map(item => (
                                        <TableRow key={item.id} hover>
                                            <TableCell>
                                                <span className={classes.eventoChip}>
                                                    {EVENTO_LABEL[item.evento] ?? item.evento}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                className={classes.nomeCell}
                                                title={item.tituloMensagem}
                                            >
                                                <strong>{item.tituloMensagem}</strong>
                                            </TableCell>
                                            <TableCell
                                                className={classes.msgCell}
                                                title={item.descricaoMensagem}
                                            >
                                                {item.descricaoMensagem}
                                            </TableCell>
                                            <TableCell
                                                style={{ fontSize: "0.78rem", lineHeight: 1.7 }}
                                            >
                                                {item.horaDisparo && (
                                                    <>
                                                        Hora: {item.horaDisparo}
                                                        <br />
                                                    </>
                                                )}
                                                {item.diaParaEnviar != null && (
                                                    <>
                                                        {DIA_LABEL[item.diaParaEnviar] ?? ""}
                                                        <br />
                                                    </>
                                                )}
                                                {item.tempoPosEvento != null &&
                                                    item.tempoPosEvento > 0 && (
                                                        <>
                                                            {item.tempoPosEvento}
                                                            {item.tempoFinal
                                                                ? ` a ${item.tempoFinal}`
                                                                : ""}{" "}
                                                            dia(s)
                                                        </>
                                                    )}
                                            </TableCell>
                                            <TableCell
                                                style={{ fontSize: "0.78rem", lineHeight: 1.7 }}
                                            >
                                                {item.criarCupom && item.nomeCupom ? (
                                                    <>
                                                        <span className={classes.cupomChip}>
                                                            {item.nomeCupom}
                                                        </span>
                                                        <br />
                                                        Validade: {item.diasValidade} dias
                                                        <br />
                                                        {item.quantCuponsEnviados} disparo(s)
                                                        <br />
                                                        {item.quantUtilizadaCupom} utilizado(s)
                                                    </>
                                                ) : (
                                                    <span style={{ color: "#bdc3c7" }}>
                                                        Sem cupom
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                <Tooltip title="Editar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => abrirEditar(item)}
                                                    >
                                                        <EditIcon
                                                            fontSize="small"
                                                            style={{ color: "#4caf50" }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Executar agora">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExecutar(item)}
                                                    >
                                                        <PlayArrowIcon
                                                            fontSize="small"
                                                            style={{ color: "#1976d2" }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExcluir(item)}
                                                    >
                                                        <DeleteIcon
                                                            fontSize="small"
                                                            style={{ color: "var(--cor-erro)" }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>
            </Box>

            <FormMarketingAutomatico
                aberto={dialogAberto}
                itemEditando={itemEditando}
                onFechar={() => setDialogAberto(false)}
            />
        </Box>
    )
}
